import * as TWEEN from '@tweenjs/tween.js';
import throttle from 'lodash/throttle';
import { chain } from 'obliterator';
import Stats from 'stats.js';
import { Color, Group, Scene, Vector3 } from 'three';
import type {
  AmbientLight,
  Box3,
  BoxHelper,
  BufferGeometry,
  Material,
  Matrix4,
  Object3D,
  OrthographicCamera,
  Sprite,
  WebGLRenderer,
} from 'three';

import {
  type CompositeProgressController,
  type SimpleProgressController,
  isAbortErrorLike,
} from '@frontend/controllers';
import { MultiMap } from '@frontend/datatypes';
import { noop } from '@frontend/utils';

import ViewPort, { INTERSECTION_SCALE_FACTOR } from '../ViewPort';
import { type ColorRGBArray, clearThreeColorCache } from '../colors';
import {
  ActiveRepresentation,
  ENTITY_TYPES,
  FRAMERATE,
  MOBILE_DEFAULTS,
  PART_TYPES,
  PART_VIEWER_MODES,
  TWEEN_TIMES,
} from '../constants';
import type { FaceJson, EdgeJson, VertexJson } from '../contracts/tess';
import {
  getBoundingBoxDiagonal,
  isCloseVectors,
  getMidpoint,
  disposeThreeJS,
} from '../geom-utils';
import { Assembly } from '../loaders/assembly';
import { AssemblyInstance } from '../loaders/assemblyInstance';
import { Body } from '../loaders/body';
import type { AnyBody } from '../loaders/body';
import { dxfService } from '../loaders/dxf/service';
import { Part } from '../loaders/part';
import type { AnyPart } from '../loaders/part';
import { STEPBody } from '../loaders/step/STEPBody';
import type { StepEdgeModel } from '../loaders/step/StepEdgeModel';
import type { StepFaceModel } from '../loaders/step/StepFaceModel';
import type { StepVertexBody } from '../loaders/step/StepVertexBody';
import type { ActiveMetaData } from '../loaders/step/utils';
import { STLBody } from '../loaders/stl/STLBody';
import { compareByOBBMaxSideArea } from '../loaders/utils';
import {
  areObjectsDifferentForKeys,
  getNodeComputedProperty,
  getRelativeCoordinates,
  getRelativeTouchCoordinates,
} from '../utils';
import { areArraysAsSetDifferent, areSetsDifferent } from '../utils';
import { viewerDataService } from '../viewerDataService';
import { prepareBoundBoxForBodies } from './boundbox-utils';
import {
  extractDefaultViewProps,
  handleZoomOnCursor,
  recenterOnIntersection,
  setUpCameraAndControls,
  getSafePositionToPreventClippingForOrthographicCamera,
  ensureCameraFarSettingCompatibleWithMaxDistance,
} from './control-utils';
import type { FeatureOrFeedBackItem } from './creation-utils';
import {
  createLight,
  createOrthoTrackball,
  createOrthographicCamera,
  createRenderer,
  createSignature,
  createSpotLight,
  getSizeAndDivisions,
} from './creation-utils';
import Cube from './cube';
import { handleUpdateExplodedState } from './explode-utils';
import {
  getAllAffectedBodies,
  handleUpdateVisibleBodies,
  setVisibilityForFocus,
  setVisibilityForObjectIds,
} from './focus-utils';
import {
  EMPTY_INTERSECTION_OBJECT,
  handleBoxSelectTotalInclusion,
  handleFirstObjectIntersect,
} from './intersection-utils';
import type {
  IntersectionTest,
  AnyIntersectionTest,
} from './intersection-utils';
import { getBodyPose } from './load-utils';
import { LoadingPriorityQueue } from './loadingPriorityQueue';
import {
  Dimensions,
  handleMeasurementCreate,
  handleMeasurementDimensionVertexUpdate,
} from './measurement-utils';
import {
  handleUpdatePaint,
  handleUpdateAdditionalFeedback,
} from './paint-utils';
import {
  RenderComponentProps,
  VECTOR_EDITOR_MODES,
} from './renderer-component';
import { handleUpdateSelectedObjectEntities } from './selection-utils';
import {
  handleFeatureSnapToUpdate,
  handleForceViewerPoseUpdate,
} from './snapto-utils';
import { handleUpdateCreateMaterialStock } from './stock-utils';
import {
  makeCameraPositionTween,
  makeUpTween,
  makeOrthoCursorTween,
  makeControlTargetTween,
} from './tween-utils';
import { handleUpdateVectors } from './vectorization-utils';
import { handleUpdateForceView } from './view-utils';

//In practice we hit 1/2 FRAMERATE; so multiplying by 2 here.
const inverseFrameRate = Math.floor(1000 / (FRAMERATE * 2));

export type MouseMarker = {
  x: number;
  y: number;
};

export type RendererState = RenderComponentProps & {
  //Note: updatePartPMIVisibility is on both state and renderer object
  //* It would be nice to have it in just one place. But need to look at setVisibilityForObjectIds()
  //  more closely and the separate of state, nextState and renderer
  updatePartPMIVisibility(state: RendererState): void;

  //Is there additional state added?
  //* There shouldn't be a need (use private/public proprties on Renderer class)
  //* this.state is mutated and I am not sure it always gets back to RendererComponent or ViewerComponent
};

type RenderableMaterial = Material & { color: Color; linewidth?: number }; //Examples: LineBasicMaterial & MeshPhysicalMaterial are materials with color

type HasGeometry = {
  geometry: BufferGeometry;
}; //Example: LineSegments | Mesh

//Note, threejs defines an Intersectable as any Object3D.
// * But when you read the docs and src it really means it implements `layers`, `children` and `raycast()`
// * And the implementations of raycast() we use require HasGeometry too.
export type Intersectable = Object3D & //Techically just `Pick<Object3D, 'layers' | 'children' | 'raycast'>` is required, but threejs types say Object3D
  HasGeometry & { boundBox?: Box3 }; //required for raycast implementations //optionally has a boundBox to help with intersection calculations

//Renderable also inclues `Group` which is used by Body, AssemblyInstance, STLBody, STEPBody because these bodies/assemblieInstances contain
//one or more Intersectable
export type Renderable = Group | Intersectable;

//HasRenderable is implemented by Body, AssemblyInstance, STLBody, STEPBody, StepVertexBody for use in composing groups of these types
export interface HasRenderable {
  renderable: Renderable;
}

//HasRenderableWithGeometry is implemented by Body, STLBody, StepVertexBody for use in composing groups of these types
//* Working to deprecate
export interface HasRenderableWithGeometry {
  activeMetaData: ActiveMetaData;
  baseColor?: Color;
  baseOpacity?: number;
  baseRGBColor?: ColorRGBArray;
  geometry: HasGeometry;
  isMaterial?: boolean;
  material: RenderableMaterial;
  //Some `meta` properties are not define for edges and vertices. Being explicit that property key is not defined here so type checking passes downstream.
  metaData: FaceJson['meta'] | EdgeJson['meta'] | VertexJson;
  renderable: Renderable;
  transform: Matrix4;
  translate(v: Vector3): void;
}

export type SavePNG = (filename: string) => void;
type HasSavePng = { __savePng: SavePNG };
export type CanvasWithSavePNG = HTMLCanvasElement & HasSavePng;

//In the scene we have a budget for the number of vertices across the various face and edge buffers across all bodies that are visible in the scene
//In future we should set this budgeted maxVerticesInBuffer by machine/gpu information
const maxVerticesAcrossAllBuffers = 25000000; //25 million (Note ul520-turbo requires about 23.7M, which influenced this 25M limit)
const maxVerticesInBody = maxVerticesAcrossAllBuffers;
const maxVerticesInSTL = maxVerticesAcrossAllBuffers;

const firstRendererId = 'firstRender';

export class Renderer {
  public state: RendererState;
  public camera: OrthographicCamera;
  public canKeyInteract = true;
  public focusedBoundBox?: BoxHelper;
  public intersectionEntities: Set<Intersectable> = new Set(); //objects must have raycast() method implemented which is what Raycaster needs for intersection tests
  public light: AmbientLight; //light is created by reference is not used
  public maxViewport: ViewPort = new ViewPort();
  public nodeHeight: number;
  public nodeWidth: number;
  public readonly group = new Group();
  public readonly scene2: Scene = new Scene();
  public readonly scene = new Scene();
  public renderer: WebGLRenderer;
  public scaleLength: number;
  public scaleRadius = 1;
  public trackball: ReturnType<typeof createOrthoTrackball>;
  private _disposed?: boolean;
  private _cachedIs2DFileType?: boolean; // Cache for 2D file type detection
  private _lastVisibleBodiesForIs2D?: string[]; // Track visibleBodies used for last is2DFileType computation
  private _lastBodyCountForIs2D?: number; // Track loaded body count for cache invalidation
  private _lastReportedDxfControlsVisible?: boolean; // Track last value sent to onDxfControlsVisibilityChange

  private loadingPriorityQueue: LoadingPriorityQueue<
    AnyPart | AnyBody | AssemblyInstance,
    RendererState,
    number //priority
  >;
  private cube: Cube;
  private boxSelectMouse: MouseMarker = { x: NaN, y: NaN }; // onMouseDown marker for box select
  private cancelLongPress = false;
  private isAnimating = false;
  private keyMap: Partial<Record<string, boolean>> = {};
  private mouseMap = 0; //bitmask of buttons down
  private mouse: MouseMarker = { x: 0, y: 0 }; // onMouseMove marker
  private renderRequested = false;
  private selectMouse: MouseMarker = { x: 0, y: 0 }; // onMouseDown marker
  private signature: Sprite;
  private signatureState: {
    cameraPosition?: Vector3;
    trackballTarget?: Vector3;
    orthoView?: ViewPort;
  } = {};
  private touchEndMouse: MouseMarker = { x: 0, y: 0 };
  private touchEndTime: number | null = null;
  private touchMoveMouse: MouseMarker = { x: 0, y: 0 };
  private touchStartMouse: MouseMarker = { x: 0, y: 0 };
  private touchStartTime: number | null = null;
  private touchTapCount = 0;
  private touchZoomStart: [MouseMarker, MouseMarker] = [
    { x: 0, y: 0 },
    { x: 0, y: 0 },
  ];

  private _axisAlignedBoundBox?: BoxHelper;
  private _axisAlignedDimensions?: Dimensions;

  private featuresAndFeedbackItemsByPartOrAsmId = new MultiMap<
    string,
    FeatureOrFeedBackItem
  >();
  private partFileUuidsWithPMI = new Set<string>();

  private firstLoad?: boolean = true;
  private renderProgress?: SimpleProgressController;
  private hasVisibleBodiesToLoad = false;

  //animate is intentionally a property and not method so its `this` is bound and can be used in requestAnimationFrame()
  private animate: () => void;
  private requestAnimationFrame: () => ReturnType<typeof requestAnimationFrame>;
  //temporarilySimplified and tooComplex are cached here because they don't update quickly enough when updated on viewer-component
  //In future this should be kept in `context`
  private temporarilySimplified: Set<string>;
  private tooComplex: Set<string>;

  private rootProgressById = new Map<string, CompositeProgressController>();
  private _node?: HTMLElement;
  public _nodeWindow?: WindowProxy & typeof globalThis;

  constructor(node: HTMLElement, state: RendererState) {
    this._node = node;
    this.state = {
      ...state,
      //Why set override these? They are provided by viewer-component.
      //Once renderer is instantiated, renderer's values superceed and then these
      //values from viewer-component are never used again. Do they need to be defined on renderer-component?
      forcedViewerPose: '',
      selectedObjectsKeys: [],
      caliperObjectsKeys: [],
    };

    this.temporarilySimplified = this.state.temporarilySimplified;
    this.tooComplex = this.state.tooComplex;

    if (state.visibleBodies?.length > 0) {
      this.hasVisibleBodiesToLoad = true;
    }

    //Instantiate the priority queue. It is populated by calling this.loadBodies()
    this.loadingPriorityQueue = new LoadingPriorityQueue<
      AnyPart | AnyBody | AssemblyInstance,
      RendererState,
      number
    >(
      this.afterEachLoaded,
      this.afterEachPriorityLevelLoaded,
      this.onLoadFailed,
      this.state.signal,
    );

    //TODO: Cleanup to make code clearer.  Separate methods from initialState and state. And if some state should be observed,
    //use an ObservableLike.

    if (window.location.hostname.includes('localhost')) {
      const stats = new Stats();
      stats.showPanel(0); // 0: fps, 1: ms, 2: mb, 3+: custom
      stats.dom.style.position = 'absolute';
      stats.dom.style.top = '0px';
      stats.dom.style.right = '0px';
      node.appendChild(stats.dom);
      this.animate = () => {
        this.renderRequested = false;
        if (this._disposed) return;
        if (this.state.isPaused) {
          this.isAnimating = false;
          return;
        }
        stats.begin();
        this.update();
        this.render();
        stats.end();
        if (this.renderProgress) {
          this.renderProgress.done();
          this.renderProgress.signal.removeUsedBy(this.state.loadingProgress);
          this.state.loadingProgress.remove(this.renderProgress);
          delete this.renderProgress;
        }
      };
    } else {
      //Same animate, but does not collect stats
      this.animate = () => {
        this.renderRequested = false;
        if (this._disposed) return;
        if (this.state.isPaused) {
          this.isAnimating = false;
          return;
        }
        this.update();
        this.render();
        if (this.renderProgress) {
          this.renderProgress.done();
          this.renderProgress.signal.removeUsedBy(this.state.loadingProgress);
          this.state.loadingProgress.remove(this.renderProgress);
          delete this.renderProgress;
        }
      };
    }
    this.requestAnimationFrame = requestAnimationFrame.bind(null, this.animate);

    this._nodeWindow = node.ownerDocument.defaultView as WindowProxy &
      typeof globalThis;
    this.nodeWidth = parseInt(getNodeComputedProperty(node, 'width'));
    this.nodeHeight = parseInt(getNodeComputedProperty(node, 'height'));

    // initial scene scaling
    this.scaleLength = getSizeAndDivisions(state.units).size;

    // world elements
    this.renderer = createRenderer(this);
    this.camera = createOrthographicCamera(this);
    createSpotLight(this);
    this.light = createLight(this);
    this.trackball = createOrthoTrackball(this);
    this.cube = new Cube(
      this,
      state.viewerMode !== PART_VIEWER_MODES.VECTOR_EDIT,
    );

    //Expose __savePng to partviewer canvas element so it can be used for cypress and unit tests
    Object.assign(this.renderer.domElement, {
      __savePng: this.savePartViewerImage.bind(this),
    } as HasSavePng);

    //Expose __savePng to cube canvas element so it can be used for cypress and unit tests
    Object.assign(this.cube.renderer.domElement, {
      __savePng: this.saveCubeImage.bind(this),
    } as HasSavePng);

    //The 'change' event listener expects () => void. So we know it will never pass a callback, so it is safe to cast as () => void
    this.trackball.addEventListener(
      'change',
      this.requestRenderIfNotRequested as () => void,
    );

    // add bodies to the scene
    this.group.name = 'sceneTop';
    this.scene.add(this.group);

    // get signature
    this.signature = createSignature();
    this.scene.add(this.signature);

    this.scene2.name = 'measurements';

    // add listeners
    this.setUpEventListeners();

    this.state.onNodeChange(this.nodeWidth, this.nodeHeight);
  }

  public get node(): HTMLElement {
    if (!this._node) {
      throw new Error('renderer was disposed');
    }
    return this._node;
  }

  public get nodeWindow(): WindowProxy & typeof globalThis {
    if (!this._nodeWindow) {
      throw new Error('renderer was disposed');
    }
    return this._nodeWindow;
  }

  // public checkIfAborted(signal: AbortSignal = this.state.signal): void | never {
  //   if (signal.aborted) {
  //     throw new Error('Parent RendererComponent aborted');
  //   }
  // }

  //Also see this.updateBoundBox
  public get axisAlignedBoundBox(): BoxHelper | undefined {
    return this._axisAlignedBoundBox;
  }

  public updateBoundBox(
    nextState: RendererState,
    additionalBodies?: Iterable<AnyBody | AssemblyInstance>,
  ) {
    let prevDiagonal: number | undefined;
    //Remove the previous boundBox (it won't be cached by the visibleBody ids)
    if (this._axisAlignedBoundBox) {
      prevDiagonal = getBoundingBoxDiagonal(
        this._axisAlignedBoundBox.geometry.boundingBox as Box3,
      );
      this._axisAlignedBoundBox.visible = false; //visibility off for safety (even though this._axisAlignedBoundBox will be discarde & replaced)
      this._axisAlignedBoundBox.clear();
      this._axisAlignedBoundBox.removeFromParent(); //Same as this.group.remove(this._axisAlignedBoundBox);
    }

    const bodyOrAssemblyInstanceById = nextState.bodyOrAssemblyInstanceById;
    const visibleBodyIds = nextState.visibleBodies;
    this._axisAlignedBoundBox = prepareBoundBoxForBodies(
      (function* () {
        for (const visibleBodyId of visibleBodyIds) {
          const visibleBodyOrAssemblyInstance =
            bodyOrAssemblyInstanceById.get(visibleBodyId);
          if (visibleBodyOrAssemblyInstance) {
            //Note: usually visibleBodies includes all descendent bodies of assemblyInstances, but not always.
            //* In online metals app, toggling visualization of the nested sheet results in only the top level visibleBodies being added
            //  to the list. Previously there was a bug in calculating the boundBox because only bodies were being yielded to prepareBoundBoxForBodies().
            //  (See setFocusVisibleForStockResult() which has `previousVisibleBodies.push(focusViewerId)`. It pushes the `focusViewerId` which
            //  could be an assembly. So it is important to yield this assemblyInstance to prepareBoundBoxForBodies())
            yield visibleBodyOrAssemblyInstance;
          }
        }
        if (additionalBodies) {
          yield* additionalBodies;
        }
      })(),
    );
    this._axisAlignedBoundBox.name = 'axisAlignedBoundBox';
    this._axisAlignedBoundBox.visible =
      nextState.showAxisAlignedBoundingBox && visibleBodyIds.length > 0;
    this.group.add(this._axisAlignedBoundBox);

    const boundingBox = this._axisAlignedBoundBox.geometry.boundingBox as Box3; //defined in prepareBoundBoxForBodies()

    this.maxViewport = ViewPort.getForBoundingBox(
      boundingBox,
      this.nodeHeight,
      this.nodeWidth,
    );

    //Note: maxBoundingBoxDiagonal is on viewer-component's state
    //* It is accessed via `load-utils.ts: encodeViewerState()`, and is required for `receiveViewerState()`, `onCaliperObjectSelect()` and `onSelectorObjectSelect()`,
    //  which the frontend app observes.
    //* unfortunately this triggers a state change in parent, causing a re-render. I'd like to call `receiveViewerState()`, `onCaliperObjectSelect()` and `onSelectorObjectSelect()` directly
    const newDiagonal = getBoundingBoxDiagonal(boundingBox);
    if (newDiagonal !== prevDiagonal) {
      //only update if it changed. This avoids unnnecessary calls to setState() which will trigger potentially infinite re-rendering
      //TODO: It updates maxBoundingBoxDiagonal on viewer-component so it can be used with encodeViewerState, but it feels like there are better ways to observe without
      //forcing re-render
      nextState.onMaxBoundingBoxDiagonalChange(
        getBoundingBoxDiagonal(boundingBox),
      );
    }

    this.updateAxisAlignedDimensions(nextState);
  }

  public get axisAlignedDimensions(): Dimensions | undefined {
    return this._axisAlignedDimensions;
  }

  public get areVisibleBodiesLoaded(): boolean {
    return this.everyVisibleBodyLoaded(this.state);
  }

  /**
   * Checks if the currently visible content is a 2D file type (DXF/DWG).
   * This is used to determine whether to apply 2D-specific viewing behaviors
   * such as fixed straight-on camera, pan-only mouse controls, and flat backgrounds.
   *
   * @returns true if ALL visible bodies are DXF type (or if there are no visible bodies yet)
   */
  public get is2DFileType(): boolean {
    const { visibleBodies, bodyOrAssemblyInstanceById } = this.state;

    // Check if visibleBodies has changed (indicates file switch)
    const visibleBodiesChanged =
      this._lastVisibleBodiesForIs2D !== visibleBodies;
    const currentBodyCount = bodyOrAssemblyInstanceById.size;

    // Quick cache check: if visibleBodies hasn't changed and body count is same,
    // return cached value without recomputing
    if (
      this._cachedIs2DFileType !== undefined &&
      !visibleBodiesChanged &&
      this._lastBodyCountForIs2D === currentBodyCount
    ) {
      return this._cachedIs2DFileType;
    }

    // If visibleBodies changed (file switch), invalidate the cache and related tracking
    // to avoid using stale values from the previous file
    if (visibleBodiesChanged) {
      this._cachedIs2DFileType = undefined;
      this._lastReportedDxfControlsVisible = undefined;
    }

    // Compute the value
    const result = this.computeIs2DFileTypeForState(this.state);

    // Update cache tracking
    this._lastVisibleBodiesForIs2D = visibleBodies;
    this._lastBodyCountForIs2D = currentBodyCount;
    this._cachedIs2DFileType = result;

    return result;
  }

  /**
   * Computes whether a view is a 2D file type for a given state.
   * This allows checking the 2D status of a state before it becomes `this.state`.
   * Used primarily in afterEachPriorityLevelLoaded where nextState isn't yet in this.state.
   */
  public computeIs2DFileTypeForState(state: RendererState): boolean {
    const {
      visibleBodies,
      bodyOrAssemblyInstanceById,
      pendingPartFileUuids,
      partOrAssemblyById,
    } = state;

    // Helper to check a part's type
    const checkPartType = (
      partOrAssembly: AnyPart | Assembly,
    ): boolean | undefined => {
      if (partOrAssembly instanceof Part) {
        if (partOrAssembly.hasType) {
          return partOrAssembly.type === PART_TYPES.DXF;
        }
        return undefined; // Type not determined yet
      }
      // Assembly is always 3D
      return false;
    };

    // Helper to check pending parts
    const checkPendingParts = (): boolean | undefined => {
      if (pendingPartFileUuids && pendingPartFileUuids.length > 0) {
        for (const uuid of pendingPartFileUuids) {
          const partOrAssembly = partOrAssemblyById.get(uuid);
          if (partOrAssembly) {
            const result = checkPartType(partOrAssembly);
            if (result !== undefined) {
              return result;
            }
          }
        }
      }
      return undefined;
    };

    // If no visible bodies yet, check pending parts first
    if (visibleBodies.length === 0) {
      const pendingResult = checkPendingParts();
      if (pendingResult !== undefined) {
        return pendingResult;
      }

      // Fallback: check all parts in partOrAssemblyById
      for (const partOrAssembly of partOrAssemblyById.values()) {
        const result = checkPartType(partOrAssembly);
        if (result !== undefined) {
          return result;
        }
      }

      // No parts loaded yet - return false
      return false;
    }

    // Check if visible bodies are loaded
    let foundAnyBody = false;
    let foundNonDxf = false;
    let foundConfirmedDxf = false;

    for (const visibleBodyId of visibleBodies) {
      const bodyOrAssemblyInstance =
        bodyOrAssemblyInstanceById.get(visibleBodyId);
      if (bodyOrAssemblyInstance) {
        foundAnyBody = true;
        if (bodyOrAssemblyInstance instanceof Body) {
          if (bodyOrAssemblyInstance.part.hasType) {
            if (bodyOrAssemblyInstance.type === PART_TYPES.DXF) {
              foundConfirmedDxf = true;
            } else {
              foundNonDxf = true;
            }
          }
        } else if (bodyOrAssemblyInstance instanceof AssemblyInstance) {
          // AssemblyInstances are always 3D
          foundNonDxf = true;
        }
      }
    }

    // If any visible body is NOT DXF, this is a 3D view
    if (foundNonDxf) {
      return false;
    }

    // If we found a confirmed DXF body, this is a 2D view
    if (foundConfirmedDxf) {
      return true;
    }

    // If visibleBodies has items but none are loaded yet, check pendingPartFileUuids
    if (!foundAnyBody) {
      const pendingResult = checkPendingParts();
      if (pendingResult !== undefined) {
        return pendingResult;
      }

      // Bodies not loaded yet - return false
      return false;
    }

    // Bodies found but no definitive type - return false
    return false;
  }

  public rotate2DView(angleRadians: number): void {
    if (!this.is2DFileType) return;

    const zAxis = new Vector3(0, 0, 1);

    // Get the geometry center from the bounding box
    const boundBox = this.focusedBoundBox ?? this._axisAlignedBoundBox;
    const geometryCenter = new Vector3();
    if (boundBox?.geometry.boundingBox) {
      boundBox.geometry.boundingBox.getCenter(geometryCenter);
    } else {
      // Fallback to current target if no bounding box
      geometryCenter.copy(this.trackball.target);
    }

    // Calculate the pan offset (how far the target is from geometry center)
    const panOffset = this.trackball.target.clone().sub(geometryCenter);

    // Rotate the pan offset around the Z axis
    panOffset.applyAxisAngle(zAxis, angleRadians);

    // Calculate new target position (geometry center + rotated pan offset)
    const newTarget = geometryCenter.clone().add(panOffset);

    // Rotate camera position around the geometry center
    const cameraToGeometry = this.camera.position.clone().sub(geometryCenter);
    cameraToGeometry.applyAxisAngle(zAxis, angleRadians);
    const newCameraPosition = geometryCenter.clone().add(cameraToGeometry);

    // Rotate the up vector
    this.camera.up.applyAxisAngle(zAxis, angleRadians);

    // Apply the new positions
    this.camera.position.copy(newCameraPosition);
    this.trackball.target.copy(newTarget);

    // Update the reference positions for reset functionality
    this.trackball.position0.copy(this.camera.position);
    this.trackball.up0.copy(this.camera.up);
    this.trackball.target0.copy(this.trackball.target);

    // Notify state of changes
    this.state.onCameraPositionChange(this.camera.position.clone());
    this.state.onCameraUpChange(this.camera.up.clone());
    this.state.onControlTargetChange(this.trackball.target.clone());
    this.requestRenderIfNotRequested();
  }

  //loadPartData(): Loads just the part data. (It does not hydrate/load the parts, bodies or assemblyInstances)
  private async loadPartData(
    partFileUuid: string,
    nextState: RendererState,
    lastPause: number = performance.now(),
    isFirstPartFileUuid = false,
  ): Promise<number> {
    if (!nextState.partOrAssemblyById.has(partFileUuid)) {
      const { signal } = this.state;
      const progressId = viewerDataService.getPartFileLoadingId(partFileUuid);
      const loadPartProgress = nextState.loadingProgress.pushCompositeProgress({
        id: progressId,
        description: 'Loading Part',
      });
      //Inform loadingProgress which visibleIds we care about. (This visibility of this work's progress impacts when the LoadingIndicator appears.)
      //Before we know what bodies to load, we need to get the part viewerData and establish the assembly structure
      this.rootProgressById.set(partFileUuid, loadPartProgress);
      const onComplete = () => {
        this.rootProgressById.delete(partFileUuid);
      };
      loadPartProgress.subscribe({ complete: onComplete, error: onComplete });
      try {
        if (nextState.partOrAssemblyById.size === 0) {
          nextState.loadingProgress.updateVisibleIds([
            progressId,
            viewerDataService.getInspectId(partFileUuid),
            viewerDataService.getPartDataId(partFileUuid),
            viewerDataService.getEstablishAssemblyStructureId(partFileUuid),
            viewerDataService.getPMIDataId(partFileUuid),
          ]);
        }
        const data = await viewerDataService.getPartFileUuid(
          partFileUuid,
          isFirstPartFileUuid,
          nextState.partOrAssemblyById,
          nextState.bodyOrAssemblyInstanceById,
          nextState.bodyIdsByPartFileUuid,
          this,
          {
            lastPause,
            signal,
            progress: loadPartProgress,
            fetchViewerData: nextState.fetchViewerData,
            useCache: nextState.useCache,
          },
        );
        if (isFirstPartFileUuid) {
          //otherwise loadPartProgress will be closed by a future
          //getRootPartFileUuidAndLoadAsyncUnhydrated() when the visibleIds merit adding
          //the work to load
          loadPartProgress.close();
        }
        if (signal.aborted) {
          return lastPause; //Stop loading if aborted
        }
        const {
          bodies: newBodies,
          assemblyInstances: newAssemblyInstances,
          rootGroups,
        } = data;
        //eslint-disable-next-line no-param-reassign
        lastPause = data.lastPause;

        nextState.bodies.push(...newBodies);
        nextState.assemblyInstances.push(...newAssemblyInstances);
        this.group.add(...rootGroups);
        this.loadFeaturesAndFeedbackItems(false);
        this.loadPMI(nextState.pmiUrlByPartFileUuid);
      } catch (e) {
        loadPartProgress.close();
        if (isAbortErrorLike(e)) {
          return lastPause; //stop loading if aborted (loadPartData will be called again shortly)
        }
        throw e;
      }
    }

    nextState.onLoadPartFile({ partFileUuid }); //doesn't hurt to call if already loaded
    return lastPause;
  }

  public async loadPartDatas(
    newPendingPartFileUuids: Iterable<string>,
    nextState: RendererState,
    lastPause: number = performance.now(),
  ): Promise<number> {
    let isFirstPartFileUuid = true;
    for (const partFileUuid of newPendingPartFileUuids) {
      try {
        //eslint-disable-next-line no-param-reassign
        lastPause = await this.loadPartData(
          partFileUuid,
          nextState,
          lastPause,
          isFirstPartFileUuid,
        );
        isFirstPartFileUuid = false;
      } catch (e) {
        if (isAbortErrorLike(e)) {
          return lastPause; //stop loading if aborted (loadPartData will be called again shortly)
        }
        throw e;
      }

      if (
        this.hasVisibleBodiesToLoad &&
        this.everyVisibleBodyConstructed(nextState) &&
        !this.loadingPriorityQueue.isRunning
      ) {
        //There is edge case where visibleBodies is set before renderer-component creates the Renderer instance
        //* This happens when the user loads a part from part library, goes back to part library, and then returns to same part
        //* Because of this the useEffect() in renderer-component that watches visibleBodies does not call loadBodies()
        //* This edge case is detected by setting this.hasVisibleBodiesToLoad so that the bodies are loaded when their part data has been loaded
        this.hasVisibleBodiesToLoad = false;
        await this.loadBodies(nextState, lastPause).catch((err) => {
          console.error(err); //Handled by logging.
          return Promise.resolve();
        });
      }

      //Otherwise, bodies will be loaded on demand
      //* After each call to this.state.onLoadPartFile(), the frontend app will consider changing visibleBodies. So when the first partFileUuid is loaded,
      //  the frontend app will set the visibleBodies, and if more visible bodies may be added after each subsequent partFileUuid is loaded.
      //  The change to visibleBodies usually occurs before the following partFileUuids are loaded. So even though the subsequent partFileUuids will be loaded
      //  their bodies won't be fully loaded until there is demand.
      //* why not call loadBodies() here after each loadPadData? Or after this loop?  Doing so has the advantage of loading more bodies; the ones in view, and the
      //  ones not in view.  And the loading indicator is only going to show the progress of the visible bodies, so non visible bodies would be loaded in
      //  background.  Yes this could be nice to have more bodies loaded pre-emptively. The UI is blocked (typically) for < 50ms, but there are edge cases when it
      //  is between 50-100ms, so there could are blips of unresponsivess loading non-visible bodies when starting to interact with visible bodies. Because loading
      //  the newly visible bodies on demand is fast, I think it is worth always loading the visible bodies on demand, and minimize loading non-visible bodies from
      //  secondary files (when possible).  As well, if a user never looks at the bodies of other secondary partFileUuids, we will haved saved memory and time required
      //  to load the other bodies.
    }

    return lastPause;
  }

  //loadBodies():
  //* Updates the priority queue with latest parts, bodies and assemblyInstances
  //  * if they already exists in queue, but priority changes, the currently loading body/assemblyInstance
  //    is paused and the next top item in queue is started or resumed
  //  * Note, when an assemblyInstance is loaded, it will load its descendants in appropriate order and this often
  //    results in other items in the queue to become `done`. So the queue's population can drop in chunks
  //* About priorities:
  //  * Parts associated with visible bodies must be loaded first.
  //    * Bodies will load the part if it is not loaded, but it is better to load the part in advance so that the progress estimate of the body
  //      can be computed sooner. (This helps eliminate jerky backtracking in the progress bar.)
  //  * Then visible bodies are loaded
  //  * Then parts associated with non-visible bodies are loaded
  //  * And finally non-visible bodies are loaded
  //* If loadBodies() is called and all parts, bodies and assemblyInstances are already loaded, then the call is effectively a noop
  public loadBodies(
    nextState: RendererState,
    lastPause: number = performance.now(),
  ): Promise<number> {
    if (
      this.hasVisibleBodiesToLoad &&
      !this.everyVisibleBodyConstructed(nextState)
    ) {
      return Promise.resolve(lastPause); //will come back to loading when bodies are constructed
    }

    if (
      areArraysAsSetDifferent(nextState.visibleBodies, this.state.visibleBodies)
    ) {
      //Abort tweening if visibleBodies changes while tweening
      TWEEN.removeAll();
      this.state.isTweening = false;
    }

    this.hasVisibleBodiesToLoad = false;
    const rootPartFileUuid: string | undefined =
      this.getRootPartFileUuidAndLoadAsyncUnhydrated(nextState);

    //Inform loadingProgress which visibleIds we care about. We care about the constiuent progresses that are for loading the visibleBodies
    if (rootPartFileUuid) {
      nextState.loadingProgress.appendVisibleIds(
        (function* () {
          const rootProgressId =
            viewerDataService.getPartFileLoadingId(rootPartFileUuid);
          nextState.loadingProgress.appendVisibleIds([
            rootProgressId,
            viewerDataService.getInspectId(rootPartFileUuid),
            viewerDataService.getPartDataId(rootPartFileUuid),
            viewerDataService.getEstablishAssemblyStructureId(rootPartFileUuid),
            viewerDataService.getPMIDataId(rootPartFileUuid),
          ]);
          yield rootProgressId;
          for (const visibleId of nextState.visibleBodies) {
            //body
            const bodyOrAssemblyInstance =
              nextState.bodyOrAssemblyInstanceById.get(visibleId);
            if (bodyOrAssemblyInstance && !bodyOrAssemblyInstance.isLoaded) {
              yield viewerDataService.getBodyOrAssemblyInstanceLoadingId(
                visibleId,
              );
              if (bodyOrAssemblyInstance instanceof Body) {
                //body's part id
                if (!bodyOrAssemblyInstance.part.isLoaded) {
                  yield viewerDataService.getPartLoadingId(
                    bodyOrAssemblyInstance.part.id as string,
                  );
                }
              } else {
                //assembly instance's id and it's descendents
                yield* bodyOrAssemblyInstance.loadingIds();
              }
            }
          }
        })(),
      ); //don't remove the ids for PMI and other non-body related
    }

    if (!this.renderProgress) {
      //Added here so that it's not first item in loadingProgress (because description comes from first not done)
      //And before the bodies finish loading
      this.renderProgress = this.state.loadingProgress.pushSimpleProgress({
        id: firstRendererId,
        todoCount: 1,
        expectedMs: 1000, //over estimated
        description: 'Rendering...',
      });
      this.state.loadingProgress.appendVisibleIds(firstRendererId);
    }

    //Update the loadingPriorityQueue and start it (if not running already)
    //* as loadables complete loading, this.afterEachLoaded and this.afterEachPriorityLevelLoaded will be called as appropriate
    return this.loadingPriorityQueue.updateAndStart(
      (function* () {
        for (const loadable of nextState.bodyOrAssemblyInstanceById.values()) {
          if (loadable.isLoaded) {
            continue;
          }
          //priority: lower numbers first
          const priority = nextState.visibleBodies.includes(
            loadable.id as string,
          )
            ? 1 //visible
            : 2; //not visible
          //sizePriority is between 0 and 1, with bodies with largest obb side first.
          //Between 0 and 1 because we are prioritizing with range between the visible and not visible values
          const obbMaxSideArea = loadable.obbMaxSideArea;
          const sizePriority =
            obbMaxSideArea > 0 ? 1 / obbMaxSideArea : 1 - Number.EPSILON;
          const bodyPriority = priority + sizePriority;
          yield { priority: bodyPriority, loadable };
          if (loadable instanceof Body) {
            //If Body, must also load the body's part (and just before the body!)
            yield {
              priority: bodyPriority - Number.EPSILON,
              loadable: loadable.part,
            };
          }
        }
      })(),
      nextState,
      lastPause,
    );
  }

  private getRootPartFileUuidAndLoadAsyncUnhydrated({
    visibleBodies,
    bodyOrAssemblyInstanceById,
  }: RendererState) {
    for (const visibleId of visibleBodies) {
      const bodyOrAssemblyInstance = bodyOrAssemblyInstanceById.get(visibleId);
      if (bodyOrAssemblyInstance) {
        const rootPartFileUuid = bodyOrAssemblyInstance.rootPartFileUuid;
        const rootBodyOrAssemblyInstanceId =
          bodyOrAssemblyInstance.rootBodyOrAssemblyInstanceId;
        const rootBodyOrAssemblyInstance = bodyOrAssemblyInstanceById.get(
          rootBodyOrAssemblyInstanceId,
        );
        if (
          rootBodyOrAssemblyInstance &&
          !rootBodyOrAssemblyInstance.isLoaded
        ) {
          const rootProgress = this.rootProgressById.get(rootPartFileUuid);
          if (rootProgress) {
            //Start loading the unhydrated descendent so rootProgress is updated with descendent progress work
            rootBodyOrAssemblyInstance.loadAsyncUnhydrated(
              performance.now(),
              rootProgress,
            );
            rootProgress.close();
          }
        }
        return rootPartFileUuid;
      }
    }
  }

  //loadFeaturesAndFeedbackItems(): loads the features and feedbackItems into parts
  public loadFeaturesAndFeedbackItems(
    updateRenderState: boolean,
    //* new features and feedbackItems
    //* These are added to the featuresAndFeedbackItemsByPartOrAsmId cache, so if the parts
    //  to load these features and feedbackItems are not loaded yet, they will be kept in cache
    //  until they are ready.
    featuresOrFeedbackItems: Iterable<FeatureOrFeedBackItem> = [],
  ): void {
    if (featuresOrFeedbackItems) {
      for (const featureOrFeedbackItem of featuresOrFeedbackItems) {
        this.featuresAndFeedbackItemsByPartOrAsmId.set(
          featureOrFeedbackItem.referenceUuid,
          featureOrFeedbackItem,
        );
      }
    }
    if (this.state.partOrAssemblyById.size === 0) {
      //if there are new features or feedbackItems, they were cached and will be loaded in future
      return;
    }
    if (this.featuresAndFeedbackItemsByPartOrAsmId.dimension > 0) {
      let changed = false;
      const {
        partOrAssemblyById,
        bodyIdsByPartFileUuid,
        bodyOrAssemblyInstanceById,
      } = this.state;
      for (const [
        id,
        featuresAndFeedbackItemsForPartOrAssembly,
      ] of this.featuresAndFeedbackItemsByPartOrAsmId.associations()) {
        const partOrAssembly = partOrAssemblyById.get(id);
        if (partOrAssembly && featuresAndFeedbackItemsForPartOrAssembly) {
          if (
            partOrAssembly &&
            featuresAndFeedbackItemsForPartOrAssembly?.length
          ) {
            changed =
              partOrAssembly.createAdditionalFeedback(
                featuresAndFeedbackItemsForPartOrAssembly,
              ) || changed;
          }
          this.featuresAndFeedbackItemsByPartOrAsmId.delete(
            partOrAssembly.id as string,
          );

          const bodyIdsForPart = bodyIdsByPartFileUuid.get(id) ?? [];
          //update bodies/assemblyInstances for the partOrAssembly
          for (const bodyOrAssemblyInstanceId of bodyIdsForPart) {
            const bodyOrAssemblyInstance = bodyOrAssemblyInstanceById.get(
              bodyOrAssemblyInstanceId,
            );
            bodyOrAssemblyInstance?.establishAdditionalFeedback();
          }
        }
      }
      if (changed && updateRenderState) {
        this.updateState({
          ...this.state,
          forceUpdateAdditionalFeedback: true,
        });
      }
    }
  }

  //loadPMI(): Loads the PMI available from URLs in pmiUrlByPartFileUuid into respective parts
  public loadPMI(pmiUrlByPartFileUuid: Record<string, string>): void {
    if (this.state.partOrAssemblyById.size === 0) {
      return;
    }
    const signal = this.state.signal;
    if (pmiUrlByPartFileUuid) {
      const loadedPMIPromises = [];
      const { loadingProgress, partOrAssemblyById, useCache } = this.state;
      for (const partOrAssembly of partOrAssemblyById.values()) {
        const partFileUuid = partOrAssembly.id as string;
        if (
          partFileUuid in pmiUrlByPartFileUuid &&
          pmiUrlByPartFileUuid[partFileUuid] && //Not null
          !this.partFileUuidsWithPMI.has(partFileUuid)
        ) {
          if (this.everyVisibleBodyConstructed(this.state)) {
            loadingProgress.appendVisibleIds([
              viewerDataService.getPMIDataId(partFileUuid),
            ]);
          }
          //Get the pmiData
          loadedPMIPromises.push(
            (async () => {
              const progress = loadingProgress.pushCompositeProgress({
                id: viewerDataService.getPMIDataId(partFileUuid),
                description: 'Fetching PMI Data',
              });
              try {
                await viewerDataService.getPMIData(
                  partOrAssembly,
                  pmiUrlByPartFileUuid[partFileUuid],
                  this.state.isPMIViewActive,
                  { signal, progress, useCache },
                );
              } catch {
                //best efforts made
              }
              progress.close();
              if (partOrAssembly.pmi?.hasAnnotations) {
                this.scene.add(partOrAssembly.pmi.geometry);
                if (this.areVisibleBodiesLoaded) {
                  this.requestRenderIfNotRequested();
                }
              }
            })(),
          );
          this.partFileUuidsWithPMI.add(partFileUuid);
        }
      }
      if (loadedPMIPromises.length > 0) {
        (async () => {
          await Promise.all(loadedPMIPromises);
          this.updateHasPMIAnnotations();
        })();
      }
    }
  }

  //updatePartPMIVisibility()
  //Given visibleBodyIds and isPMIViewActive, updates the visibility of the PMI geometry
  public updatePartPMIVisibility(state: RendererState = this.state): boolean {
    let changed = false;
    const {
      partOrAssemblyById,
      visibleBodies,
      focusedBodies,
      isPMIViewActive,
    } = state;
    if (partOrAssemblyById) {
      if (visibleBodies?.length > 0) {
        for (const bodyOrAssemblyInstance of this.state.bodyOrAssemblyInstanceById.values()) {
          const { pmi } =
            (bodyOrAssemblyInstance as AnyBody).part ||
            (bodyOrAssemblyInstance as AssemblyInstance).assembly ||
            {};
          if (pmi) {
            const id = bodyOrAssemblyInstance.id as string;
            if (!(visibleBodies.includes(id) || focusedBodies.includes(id))) {
              if (pmi.isVisible) {
                pmi.isVisible = false;
                changed = true;
              }
            } else if (pmi.isVisible !== isPMIViewActive) {
              pmi.isVisible = !!isPMIViewActive;
              changed = true;
            }
          }
        }
      } else {
        for (const { pmi } of partOrAssemblyById.values()) {
          if (pmi?.isVisible) {
            pmi.isVisible = false;
            changed = true;
          }
        }
      }
      this.updateHasPMIAnnotations(state);
      if (changed) {
        this.requestRenderIfNotRequested();
      }
    }
    return changed;
  }

  public updateState(
    nextState: RendererState,
    isAfterLoadBodies = false,
  ): void {
    //Note: Sometimes page loads with this.state.visibleBodies set to the visibleBodies for the previous partViewer, so test bodies are constructed (this.loadPartDatas() ran)
    if (this.everyVisibleBodyConstructed(nextState)) {
      if (!isAfterLoadBodies) {
        //then this.updateState() was called from renderer-component.tsx
        //* It's now time to loadBodies() if the visibleBodies changed. (When loadBodies() is finished, this.updateState() will be called again to follow up with remaining work)
        if (
          areArraysAsSetDifferent(
            this.state.visibleBodies,
            nextState.visibleBodies,
          )
        ) {
          // Will wait for renderer-component.tsx useEffect that is watching visibleBodies to loadBodies, after which updateState will be called again with
          // isAfterLoadBodies === true. The reason it is not called here is because updateState() fires too frequently because the useEffect that calls
          // renderer.updateState() is watching for changes on MANY properties. And because loadBodies is async and it does not update the state until
          // it is done, updateState() gets called many times where differentVisibleBodies will be true because we have not updated visibleBodies in this.state,
          // and this ends up causing a flood of react setState() calls in viewer-component. Yes - this is a smell about the organization/architecture.
          // There is an impediance mismatch... We need to move some observable state to new homes, and leverage useContext or other observable mechanisms
          // that don't cause unnecesseary re-rendering
          this.loadingPriorityQueue.updateState(nextState);
          return;
        }
        //otherwise, fall through with remaining update
      }
    }

    // TODO: clean up this tweening update function
    if (nextState.isTweening) {
      handleMeasurementDimensionVertexUpdate(this, nextState);
      // we call this function for the sake of showing feature/feedback that have ephemerally loaded
      // while a comment tween is underway
      handleUpdateAdditionalFeedback(this.state, nextState);

      // need to update manually the things that tweening affects
      this.state = {
        ...this.state,
        //Why?: this.state doesn't need to be cloned
        //These values are updated in viewer-component via various callbacks, but not here
        cameraPosition: nextState.cameraPosition.clone(),
        cameraUp: nextState.cameraUp.clone(),
        cameraZoom: nextState.cameraZoom,
        controlTarget: nextState.controlTarget.clone(),
        orthoView: nextState.orthoView.clone(),
        // TODO: add other things that the handle function above pertain to for updating
        showAdditionalKeys: nextState.showAdditionalKeys,
        showAdditionalKeysExplicit: nextState.showAdditionalKeysExplicit,
        forceUpdateAdditionalFeedback: nextState.forceUpdateAdditionalFeedback,
      };
      this.requestRenderIfNotRequested();
      return;
    }

    const notIsLoading = !nextState.loadingProgress.isLoadingVisible;
    if (notIsLoading && !this.state.orthoView.isEmpty()) {
      if (handleForceViewerPoseUpdate(this, nextState)[0]) return;
    }

    if (notIsLoading) {
      handleFeatureSnapToUpdate(this, nextState);
    }

    if (!isAfterLoadBodies && !nextState.isPaused) {
      handleUpdateVisibleBodies(this, nextState);
      this.updateFocusedBoundBoxAndViewPort(nextState, false);
    }

    if (!nextState.isPaused) {
      handleUpdateForceView(this, nextState);
    }

    if (!nextState.isPaused) {
      handleUpdateSelectedObjectEntities(this.state, nextState);
    }

    // showing additional feedback information
    if (!nextState.isPaused) {
      handleUpdateAdditionalFeedback(this.state, nextState);
    }

    if (!nextState.isPaused) {
      handleUpdateExplodedState(this.state, nextState);
      if (this._axisAlignedBoundBox) {
        //visibleBodies did not change, so no need to this.updateBoundBox.
        //Only need to toggle its visibility
        const previousVisibility = this._axisAlignedBoundBox.visible;
        this._axisAlignedBoundBox.visible =
          nextState.showAxisAlignedBoundingBox &&
          nextState.visibleBodies.length > 0;
        if (previousVisibility !== this._axisAlignedBoundBox.visible) {
          this.updateAxisAlignedDimensions(nextState); //will remove or update _axisAlignedDimensions
        }
      } else {
        this.updateAxisAlignedDimensions(nextState); //will remove _axisAlignedDimensions
      }
    }

    // If new update commands for Vectors in vectorEditor, handle StepEdgeModel setters
    // Iterate bodies to access all edge maps
    if (
      nextState.updatedVectors.length &&
      nextState.updatedVectorsCounter &&
      areObjectsDifferentForKeys(this.state, nextState, ['updatedVectors'])
    ) {
      nextState.bodies.forEach((body: Body<PART_TYPES.DXF>) => {
        handleUpdateVectors(
          nextState.updatedVectors,
          body.obj,
          nextState.updatedVectorsCounter,
        );
      });
    }

    // If export dxf flow requested, call serializeDXF on part and return the resulting DXF Blob
    if (
      this.state.receiveSerializedDXF &&
      nextState.isDxfFinalizing &&
      this.state.isDxfFinalizing !== nextState.isDxfFinalizing
    ) {
      // TODO: may need to check here: should only be one body
      // using serializeBody because updated edgeStyleByEdgeName only exists on STEPBody, not on part metadata
      const dxf = dxfService.serializeBodyForLayer(nextState.bodies[0]);
      if (dxf) {
        this.state.receiveSerializedDXF({ dxf });
      }
    }

    // highlight, wireframe
    if (!nextState.isPaused) {
      handleUpdatePaint(this.state, nextState);
    }

    // caliper tool measurements
    if (notIsLoading) {
      handleMeasurementCreate(this, nextState);
    }

    if (!nextState.isPaused) {
      handleMeasurementDimensionVertexUpdate(this, nextState);
    }

    // material stock creation logic (for rod and bar material)
    handleUpdateCreateMaterialStock(this, nextState);

    const shouldRun =
      !nextState.isPaused &&
      this.state.isPaused &&
      !this.isAnimating &&
      this.everyVisibleBodyLoaded(nextState);

    // do not update the forced pose, as that will happen in the update handler
    // unless next state is null
    // also dont update historical selections unless we arent loading
    // NOTE: we do this for the condition when these props get set at a higher level
    // while the scene is loading
    this.state = {
      ...nextState,

      //Why?: this.state doesn't need to be cloned
      //These values are updated in viewer-component via various callbacks, but not here
      forcedViewerPose: nextState.forcedViewerPose
        ? this.state.forcedViewerPose
        : nextState.forcedViewerPose,
      selectedObjectsKeys: !notIsLoading
        ? this.state.selectedObjectsKeys
        : nextState.selectedObjectsKeys,
      caliperObjectsKeys: !notIsLoading
        ? this.state.caliperObjectsKeys
        : nextState.caliperObjectsKeys,
    };

    // Check is2DFileType and update related state if it changed
    // The getter has its own caching that makes this fast when nothing has changed
    const currentIs2DFileType = this.is2DFileType;
    if (this._lastReportedDxfControlsVisible !== currentIs2DFileType) {
      this._lastReportedDxfControlsVisible = currentIs2DFileType;
      this.scene.background = currentIs2DFileType ? new Color(0xf2f2f2) : null;
      this.state.onDxfControlsVisibilityChange?.(currentIs2DFileType);

      // Update trackball rotation setting when 2D state changes
      // For 2D files: disable mouse rotation (use toolbar buttons only)
      // For 3D files: enable mouse rotation (unless in VECTOR_EDIT mode)
      const isVectorEdit =
        this.state.viewerMode === PART_VIEWER_MODES.VECTOR_EDIT;
      this.trackball.noRotate = currentIs2DFileType || isVectorEdit;
    }

    if (shouldRun) {
      this.onResize();
      this.run();
    } else {
      this.requestRenderIfNotRequested();
    }
  }

  //requestRenderIfNotRequested is intentionally a property and not method so its `this` is bound and can be used as event listener
  public requestRenderIfNotRequested = (): void => {
    if (!this.renderRequested) {
      this.renderRequested = true;
      // setTimeout is used to cap the framerate and control the default animation speed
      //setTimeout(this.requestAnimationFrame, inverseFrameRate);
      this.requestAnimationFrame(); //Not capped by framerate
    }
  };

  public get disposed(): boolean {
    return !!this._disposed;
  }

  public dispose() {
    if (this._disposed || !this._nodeWindow) return;
    this._disposed = true;
    this.cube.dispose();
    this._node?.removeChild(this.renderer.domElement);

    this.intersectionEntities.clear();
    this.signatureState = {};
    this._axisAlignedDimensions?.dispose();
    this.renderProgress?.dispose();
    this.temporarilySimplified.clear();
    this.tooComplex.clear();
    this.rootProgressById.clear();

    const {
      bodies,
      assemblyInstances,
      partOrAssemblyById,
      bodyOrAssemblyInstanceById,
      bodyIdsByPartFileUuid,
    } = this.state;

    //Remove references to parts, assemblies, assemblyInstances and bodies
    for (const objectsToDispose of [
      bodies,
      assemblyInstances,
      partOrAssemblyById.values(),
    ]) {
      for (const o of objectsToDispose) {
        o.dispose();
      }
    }
    assemblyInstances.length = 0;
    bodies.length = 0;
    partOrAssemblyById.clear();
    bodyOrAssemblyInstanceById.clear();
    bodyIdsByPartFileUuid.clear();
    clearThreeColorCache();

    disposeThreeJS(this.scene, this.scene2, this._axisAlignedBoundBox);

    this.renderer.dispose();

    this.state.loadingProgress.clear();
    this.loadingPriorityQueue.clear();

    //Wouldn't it be better to use ResizeObserver on the container?
    //Resize on window object is going to be problematic
    const nodeWindow = this._nodeWindow;
    const domElement = this.renderer.domElement;
    const trackball = this.trackball;
    nodeWindow.removeEventListener('resize', this.onResize);
    domElement.removeEventListener('mousedown', this.onMouseDown);
    nodeWindow.removeEventListener('mousedown', this.onMouseDownWindow);
    domElement.removeEventListener('mouseup', this.onMouseUp);
    domElement.removeEventListener('mousemove', this.onMouseMove);
    nodeWindow.removeEventListener('keyup', this.onKeyUp);
    nodeWindow.removeEventListener('keydown', this.onKeyDown);
    domElement.removeEventListener('touchstart', this.onTouchStart);
    domElement.removeEventListener('touchmove', this.onTouchMove);
    domElement.removeEventListener('touchend', this.onTouchEnd);
    domElement.removeEventListener('wheel', this.onMouseWheel);
    domElement.removeEventListener('dblclick', this.onDoubleClick);
    trackball.removeEventListener(
      'change',
      this.requestRenderIfNotRequested as () => void,
    );
    trackball.dispose();
    this.onResize =
      this.onMouseDown =
      this.onMouseDownWindow =
      this.onMouseUp =
      this.onKeyUp =
      this.onKeyDown =
      this.onTouchStart =
      this.onTouchMove =
      this.onTouchEnd =
      this.onMouseWheel =
      this.onDoubleClick =
      this.requestRenderIfNotRequested =
      this.animate =
      this.requestAnimationFrame =
        noop;

    this.partFileUuidsWithPMI.clear();
    this.featuresAndFeedbackItemsByPartOrAsmId.clear();

    for (const key of Object.keys(this.state) as (keyof RendererState)[]) {
      delete this.state[key];
    }
    delete this._node;
  }

  // Timeout tracker for the scheduled debounced resize
  private _cleanUpResize: NodeJS.Timeout | number = -1;

  private setUpEventListeners(): void {
    if (!this._nodeWindow) return;

    const boundResize = this.onResize.bind(this);
    // Throttle all calls to onResize. 50ms is arbitrarily chosen, but provides a smooth enough throttle experience.
    this.onResize = throttle(() => {
      // NB: We are creating a scheduled resize to fire 200ms after the last resize call. Because we have poor event
      // handling across the viewer, this guarantees a final resize will get called during resize events in quick succession

      // If we have a scheduled resize, cancel it
      if (this._cleanUpResize !== -1) {
        clearTimeout(this._cleanUpResize);
      }

      // Schedule a resize to run after 300ms
      this._cleanUpResize = setTimeout(() => {
        // On timeout, reset the resize id
        this._cleanUpResize = -1;

        // Run the scheduled resize
        boundResize();
      }, 200);

      // Run the regular resize
      // NB: This wrapping the bound onResize call in a 1 frame setTimeout to ensure it is called on a single frame delay is a hack,
      // but helps to maintain correct behavior in a code environment with inconsistent async handling patterns.
      setTimeout(boundResize);
    }, 50);

    const domElement = this.renderer.domElement;
    const nodeWindow = this._nodeWindow;
    nodeWindow.addEventListener('resize', this.onResize);
    domElement.addEventListener('mousedown', this.onMouseDown);
    nodeWindow.addEventListener('mousedown', this.onMouseDownWindow);
    domElement.addEventListener('mouseup', this.onMouseUp);
    domElement.addEventListener('mousemove', this.onMouseMove);
    nodeWindow.addEventListener('keyup', this.onKeyUp);
    nodeWindow.addEventListener('keydown', this.onKeyDown);
    domElement.addEventListener('touchstart', this.onTouchStart);
    domElement.addEventListener('touchmove', this.onTouchMove);
    domElement.addEventListener('touchend', this.onTouchEnd);
    domElement.addEventListener('wheel', this.onMouseWheel);
    domElement.addEventListener('dblclick', this.onDoubleClick);
  }

  //onMouseWheel is intentionally a property and not method so its `this` is bound and can be used as event listener
  private onMouseWheel = (e: WheelEvent): void => {
    if (!this._node) return;
    if (this.trackball.enabled && !this.trackball.noZoom) return;
    e.preventDefault();
    const coordinates = getRelativeCoordinates(e, this._node);
    //transform x, y into a range between -1 and 1
    const mX = (coordinates.x / this.nodeWidth) * 2 - 1;
    const mY = (coordinates.y / this.nodeHeight) * -2 + 1;

    const isPositiveZoom = e.deltaY > 0;
    const contraction = Math.pow(0.95, this.trackball.zoomSpeed);
    const dollyDelta = isPositiveZoom ? contraction : 1 / contraction;

    handleZoomOnCursor(mX, mY, dollyDelta, this);

    this.requestRenderIfNotRequested();
  };

  private clearBoxSelectMouse(): void {
    this.boxSelectMouse = { x: NaN, y: NaN };
  }

  //onMouseMove is intentionally a property and not method so its `this` is bound and can be used as event listener
  private onMouseMove = (e: MouseEvent): void => {
    if (!this._node) return;
    if (!this.state.isFocused) {
      this.state.onFocusChange(true);
    }
    this.cube.handleIntersectionUpdate(null);
    const coordinates = getRelativeCoordinates(e, this._node);
    //transform x, y into a range between -1 and 1
    this.mouse.x = (coordinates.x / this.nodeWidth) * 2 - 1;
    this.mouse.y = (coordinates.y / this.nodeHeight) * -2 + 1;

    // Determine if X,Y are within rendered canvas of cube
    // if yes, handle in cube,
    // else rest

    if (this.mouseMap === 0) {
      //no mouse button is down
      this.handleObjectHover(this.mouse);
    }
    if (
      this.state.editorMode !== VECTOR_EDITOR_MODES.SCALE && // disable box select in new Vector Editor Scale mode
      (this.keyMap.ControlLeft || this.keyMap.ControlRight)
    ) {
      this.state.onBoxSelectMove({ pageX: e.pageX, pageY: e.pageY });
    }
  };

  //onMouseDown is intentionally a property and not method so its `this` is bound and can be used as event listener
  private onMouseDown = (e: MouseEvent): void => {
    if (!this._node) return;
    e.preventDefault();
    this.state.closePopover();
    const coordinates = getRelativeCoordinates(e, this._node);
    //transform x, y into a range between -1 and 1
    this.selectMouse.x = (coordinates.x / this.nodeWidth) * 2 - 1;
    this.selectMouse.y = (coordinates.y / this.nodeHeight) * -2 + 1;
    this.mouseMap = e.buttons;

    if (
      this.state.editorMode !== VECTOR_EDITOR_MODES.SCALE && // disable box select in new Vector Editor Scale mode
      (this.keyMap.ControlLeft || this.keyMap.ControlRight)
    ) {
      //transform x, y into a range between -1 and 1
      this.boxSelectMouse.x = (coordinates.x / this.nodeWidth) * 2 - 1;
      this.boxSelectMouse.y = (coordinates.y / this.nodeHeight) * -2 + 1;
      this.state.onBoxSelectStart({ pageX: e.pageX, pageY: e.pageY });
    }
  };

  // onMouseDownWindow is intentionally a property and not method so its `this` is bound and can be used as event listener.
  // onMouseDownWindow only gets called when the sidebar is opened or closed.
  private onMouseDownWindow = (): void => {
    if (this.state.isPaused) return;

    if (this.state.isFocused) {
      this.state.onFocusChange(false);
    }
    // NOTE: we are doing this to account for the expand collapse action that has a 300 ms time
    // TODO: Handling with timeouts is not ideal.
    setTimeout(this.onResize, 300);
  };

  //onMouseUp is intentionally a property and not method so its `this` is bound and can be used as event listener
  private onMouseUp = (e: MouseEvent): void => {
    e.preventDefault();
    //isPrimaryUp, isMiddleUp, and isRightUp: compare before to now
    const isPrimaryUp = this.mouseMap & 1 && !(e.buttons & 1);
    const isMiddleUp = this.mouseMap & 4 && !(e.buttons & 4);
    const isRightUp = this.mouseMap & 2 && !(e.buttons & 2);
    this.mouseMap = e.buttons; //next buttons down

    // perform box select event logic if CTRL key is down and we have an initial box
    if (
      this.state.editorMode !== VECTOR_EDITOR_MODES.SCALE && // disable box select in new Vector Editor Scale mode
      this.state.activeSelectorTool &&
      (this.keyMap.ControlLeft || this.keyMap.ControlRight) &&
      this.boxSelectMouse.x
    ) {
      const intersectedEntities = handleBoxSelectTotalInclusion(
        this.boxSelectMouse,
        this.mouse,
        this,
      );

      // perform box deselect event logic if CTRL + SHIFT key is down and we have an initial box
      if (this.keyMap.ShiftLeft || this.keyMap.ShiftRight) {
        this.state.onBoxDeselect({ selections: intersectedEntities });
      } else {
        this.state.onBoxSelect({ selections: intersectedEntities });
      }

      this.state.onBoxSelectClear();
      this.clearBoxSelectMouse();

      // do not perform other select logic if box select active
      return;
    }

    const delX = this.mouse.x - this.selectMouse.x;
    const delY = this.mouse.y - this.selectMouse.y;

    // only want to perform event logic if things are close to each other
    const dist = Math.sqrt(delX * delX + delY * delY);
    if (dist > 0.02) return;

    const intersectionTest = handleFirstObjectIntersect(this.mouse, this);

    if (isPrimaryUp) {
      this.state.closePopover();
      this.onSelect(intersectionTest);
    } else if (isMiddleUp) {
      // recenter for center scroll wheel
      this.state.closePopover();
      recenterOnIntersection(intersectionTest, this);
    } else if (isRightUp) {
      // open up pop up menu for 3d space actions
      this.state.onRightClick({
        intersectionTest,
        pageX: e.pageX,
        pageY: e.pageY,
      });
    }
  };

  //onTouchMove is intentionally a property and not method so its `this` is bound and can be used as event listener
  private onTouchMove = (e: TouchEvent): void => {
    if (!this._node) return;
    e.preventDefault();
    const coordinates = getRelativeTouchCoordinates(e, this._node);
    const [touchStart, touchEnd] = coordinates;
    const touchStartX = touchStart.x;
    const touchStartY = touchStart.y;
    //transform x, y into a range between -1 and 1
    this.touchMoveMouse.x = (touchStartX / this.nodeWidth) * 2 - 1;
    this.touchMoveMouse.y = (touchStartY / this.nodeHeight) * -2 + 1;

    const delX = this.touchStartMouse.x - this.touchMoveMouse.x;
    const delY = this.touchStartMouse.y - this.touchMoveMouse.y;
    const dist = Math.sqrt(delX * delX + delY * delY);
    if (dist > 0.05) {
      this.cancelLongPress = true;
      this.state.closePopover();
    }

    if (this.trackball.enabled && !this.trackball.noZoom) return;
    if (e.touches.length === 1) return;
    // custom mobile zooming
    // establish center point to zoom towards from touch start
    const x0 = this.touchZoomStart[0].x;
    const x1 = this.touchZoomStart[1].x;
    const y0 = this.touchZoomStart[0].y;
    const y1 = this.touchZoomStart[1].y;
    const screenZoomCenter = {
      x: 0.5 * (x0 + x1),
      y: 0.5 * (y0 + y1),
    };
    //transform x, y into a range between -1 and 1
    const mX = (screenZoomCenter.x / this.nodeWidth) * 2 - 1;
    const mY = (screenZoomCenter.y / this.nodeHeight) * -2 + 1;
    const startDx = x0 - x1;
    const startDy = y0 - y1;
    const startDistance = Math.sqrt(startDx * startDx + startDy * startDy);
    const nowDx = touchStartX - touchEnd.x;
    const nowDy = touchStartY - touchEnd.y;
    const nowDistance = Math.sqrt(nowDx * nowDx + nowDy * nowDy);

    const dollyDelta = Math.pow(
      nowDistance / startDistance,
      this.trackball.zoomSpeed,
    );

    this.touchZoomStart = coordinates;

    handleZoomOnCursor(mX, mY, dollyDelta, this);

    this.requestRenderIfNotRequested();
  };

  //onTouchStart is intentionally a property and not method so its `this` is bound and can be used as event listener
  private onTouchStart = (e: TouchEvent): void => {
    if (!this._node) return;
    e.preventDefault();
    this.cancelLongPress = false;
    const coordinates = getRelativeTouchCoordinates(e, this._node);
    const [touchStart] = coordinates;
    const touchStartX = touchStart.x;
    const touchStartY = touchStart.y;
    //transform x, y into a range between -1 and 1
    this.touchStartMouse.x = (touchStartX / this.nodeWidth) * 2 - 1;
    this.touchStartMouse.y = (touchStartY / this.nodeHeight) * -2 + 1;
    this.touchMoveMouse.x = (touchStartX / this.nodeWidth) * 2 - 1;
    this.touchMoveMouse.y = (touchStartY / this.nodeHeight) * -2 + 1;
    this.touchZoomStart = coordinates;
    if (e.touches.length === 1) {
      this.touchStartTime = performance.now();
      setTimeout(() => {
        if (!this.cancelLongPress) {
          // open up pop up menu for 3d space actions
          const intersectionTest = handleFirstObjectIntersect(
            this.touchStartMouse,
            this,
          );
          this.state.onRightClick({
            intersectionTest,
            pageX: e.changedTouches[0].pageX,
            pageY: e.changedTouches[0].pageY,
          });
        }
      }, MOBILE_DEFAULTS.LONG_PRESS_TIMEOUT);
    } else {
      this.touchStartTime = null;
    }
  };

  //onTouchEnd is intentionally a property and not method so its `this` is bound and can be used as event listener
  private onTouchEnd = (e: TouchEvent): void => {
    e.preventDefault();
    // prevent two finger touches from counting
    let isDoubleTap = false;
    this.cancelLongPress = true;
    if (e.touches.length === 0) {
      const newTouchEndTime = performance.now();
      if (
        newTouchEndTime - (this.touchEndTime as number) >
        MOBILE_DEFAULTS.DOUBLE_TAP_TIMEOUT
      ) {
        this.touchTapCount = 0;
      }
      if (this.touchTapCount === 0) {
        this.touchTapCount += 1;
      } else {
        this.touchTapCount = 0;
        // also make sure location wise the touch end mouse is close
        const dist = Math.sqrt(
          Math.pow(this.touchEndMouse.x - this.touchMoveMouse.x, 2) +
            Math.pow(this.touchEndMouse.y - this.touchMoveMouse.y, 2),
        );
        if (dist < 0.05) {
          isDoubleTap = (this.touchEndTime &&
            newTouchEndTime - (this.touchEndTime as number) <
              MOBILE_DEFAULTS.DOUBLE_TAP_TIMEOUT) as boolean;
        }
      }
      this.touchEndMouse = {
        x: this.touchMoveMouse.x,
        y: this.touchMoveMouse.y,
      };
      this.touchEndTime = newTouchEndTime;
    }

    // if its a double tap we want to recenter the view
    if (isDoubleTap) {
      this.state.closePopover();
      const intersectionTest = handleFirstObjectIntersect(
        this.touchMoveMouse,
        this,
      );
      recenterOnIntersection(intersectionTest, this);
    } else {
      const dist = Math.sqrt(
        Math.pow(this.touchStartMouse.x - this.touchMoveMouse.x, 2) +
          Math.pow(this.touchStartMouse.y - this.touchMoveMouse.y, 2),
      );
      // only want to perform event logic if things are close to each other
      if (dist > 0.05) return;

      const intersectionTest = handleFirstObjectIntersect(
        this.touchMoveMouse,
        this,
      );

      let isLongPress = false;
      if (e.touches.length === 0) {
        const newTouchEndTime = performance.now();
        if (
          this.touchStartTime &&
          newTouchEndTime - this.touchStartTime >
            MOBILE_DEFAULTS.LONG_PRESS_TIMEOUT
        ) {
          isLongPress = true;
        }
      }

      if (!isLongPress) {
        this.state.closePopover();
        this.onSelect(intersectionTest);
      }
    }
  };

  //onDoubleClick is intentionally a property and not method so its `this` is bound and can be used as event listener
  private onDoubleClick = (e: MouseEvent): void => {
    if (!this._node) return;
    const coordinates = getRelativeCoordinates(e, this._node);
    //transform x, y into a range between -1 and 1
    const pos = {
      x: (coordinates.x / this.nodeWidth) * 2 - 1,
      y: (coordinates.y / this.nodeHeight) * -2 + 1,
    };
    const intersectionTest = handleFirstObjectIntersect(pos, this);
    recenterOnIntersection(intersectionTest, this);
  };

  private onSelect<T extends PART_TYPES>(
    intersectionTest: IntersectionTest<T>,
  ): void {
    if (
      !this.state.showSelectedObjects ||
      (this.state.editorMode && intersectionTest.type === ENTITY_TYPES.VERTEX)
    )
      return;
    const obj = intersectionTest.object;
    if ((obj as StepVertexBody | StepEdgeModel | StepFaceModel)?.isMaterial)
      return;
    if (this.state.activeCaliperTool) {
      if (
        this.state.editorMode === VECTOR_EDITOR_MODES.SCALE &&
        this.state.caliperObjects.length === 2
      ) {
        // In new Vector Editor: on next click AFTER creating measurement (in Scale mode), switch back to SelectionTool
        this.state.enableSelectorTool && this.state.enableSelectorTool();
      }
      this.state.onObjectClickForCaliperTool({
        ...intersectionTest,
        obj,
      });
    } else if (this.state.activeSelectorTool) {
      this.state.onObjectClickForSelectorTool({
        ...intersectionTest,
        obj,
      });
    } else {
      if (this.state.onNewMetaData) {
        const metaData =
          (obj as StepVertexBody | StepEdgeModel | StepFaceModel)?.metaData ??
          {};
        this.state.onNewMetaData({ metaData });
      }
    }
  }

  private switchSignature(nextVisibility: boolean): void {
    if (nextVisibility !== this.signature.visible) {
      this.signature.visible = nextVisibility;
      this.group.visible = !nextVisibility;

      if (nextVisibility) {
        this.signatureState = {
          cameraPosition: this.camera.position.clone(),
          trackballTarget: this.trackball.target.clone(),
          orthoView: this.state.orthoView.clone(),
        };

        this.trackball.target = new Vector3();
        this.camera.position.copy(new Vector3(100, 100, 100));
        const viewport = ViewPort.getForBoundingBoxDiagonal(
          100,
          this.nodeHeight,
          this.nodeWidth,
        );
        viewport.updateObjectProps(this.camera);
        this.camera.updateProjectionMatrix();
      } else {
        this.camera.position.copy(
          this.signatureState.cameraPosition as Vector3,
        );
        this.trackball.target.copy(
          this.signatureState.trackballTarget as Vector3,
        );
        (this.signatureState.orthoView as ViewPort).updateObjectProps(
          this.camera,
        );
        this.camera.updateProjectionMatrix();
      }

      this.requestRenderIfNotRequested();
    }
  }

  //onKeyDown is intentionally a property and not method so its `this` is bound and can be used as event listener
  private onKeyDown = (e: KeyboardEvent): void => {
    if (!this.state.isFocused) return;
    if (!this.canKeyInteract) return;
    if (this.state.isPaused) return;
    this.keyMap[e.code] = true;
    if (
      (this.keyMap.ShiftLeft || this.keyMap.ShiftRight) &&
      this.keyMap.KeyA &&
      this.keyMap.KeyD &&
      this.keyMap.KeyW
    ) {
      //Triggers easter egg "Geometry Drives Everything"
      this.switchSignature(true);
    }

    switch (e.code) {
      case 'ControlLeft':
      case 'ControlRight':
        // For VECTOR_EDIT or when selector tool is active, enable rotation on Ctrl press
        // For 2D files, rotation is already enabled with 2D constraint, so no change needed
        if (!this.is2DFileType) {
          this.trackball.noRotate =
            this.state.viewerMode === PART_VIEWER_MODES.VECTOR_EDIT ||
            (this.state.activeSelectorTool as boolean);
        }
        break;
    }
  };

  //onKeyUp is intentionally a property and not method so its `this` is bound and can be used as event listener
  private onKeyUp = (e: KeyboardEvent): void => {
    if (!this.state.isFocused) return;
    if (!this.canKeyInteract) return;
    if (this.state.isPaused) return;
    this.keyMap[e.code] = false;
    switch (e.code) {
      case 'ShiftLeft':
      case 'ShiftRight':
      case 'KeyA':
      case 'KeyD':
      case 'KeyW':
        //Triggers easter egg "Geometry Drives Everything" off
        this.switchSignature(false);
        break;
      case 'ControlLeft':
      case 'ControlRight':
        this.clearBoxSelectMouse();
        this.state.onBoxSelectClear();
        // For 2D files, keep rotation enabled with 2D constraint
        // For VECTOR_EDIT, disable rotation on Ctrl release
        if (!this.is2DFileType) {
          this.trackball.noRotate =
            this.state.viewerMode === PART_VIEWER_MODES.VECTOR_EDIT;
        }
        break;
    }
  };

  //TODO: swapVisibility is not used
  // public swapVisibility(objectIds: string[]): void {
  //   const { bodyOrAssemblyInstanceById } = this.state;
  //   const bodyObjects: (Body<T> | AssemblyInstance)[] = [];
  //   for (const bodyOrAssemblyInstance of bodyOrAssemblyInstanceById.values()) {
  //     const { id, group, isMaterial } = bodyOrAssemblyInstance;
  //     if (objectIds.includes(id as string)) {
  //       group.visible = !group.visible;
  //     }
  //     if (!isMaterial) {
  //       bodyObjects.push(bodyOrAssemblyInstance);
  //     }
  //   }
  //   this.state.onBodyPoseChange(getBodyPose(bodyObjects));
  // }

  private _prevAABB?: Box3;

  private onResize = (): void => {
    if (this.state.isPaused || !this._node) return;
    // update cameras and such
    const oldWidth = this.nodeWidth;
    const oldHeight = this.nodeHeight;
    const newWidth = parseInt(getNodeComputedProperty(this._node, 'width'));
    const newHeight = parseInt(getNodeComputedProperty(this._node, 'height'));

    // NOTE: when display: none is triggered, the window computed size
    // failes, so the browser defaults size to 100px by 100px, so if we find
    // this combo we will not perform resize action
    if (newWidth === 100 && newHeight === 100) return;

    // TODO: fix root disposal issue, but on unmount node calculation goes to NaN
    if (!newWidth || !newHeight) return;

    // Track if the size has not changed
    // NB: onResize gets used in many cases where there actually hasn't been a resize, so we need this
    const sizeDidNotChange = oldWidth === newWidth && oldHeight === newHeight;

    // Update to new size
    this.nodeWidth = newWidth;
    this.nodeHeight = newHeight;
    this.state.onNodeChange(newWidth, newHeight);

    if (!this.areVisibleBodiesLoaded) return;

    if (this._axisAlignedBoundBox) {
      // Pull out the current AABB
      const currentAABB = this._axisAlignedBoundBox.geometry
        .boundingBox as Box3;
      // Pull out past and current checks for emptiness of AABBs
      // NB: Box3.isEmpty() does not seem to actually check that both bounds are zeroed out
      const wasBoxEmpty =
        this._prevAABB &&
        new Vector3().equals(this._prevAABB.min) &&
        new Vector3().equals(this._prevAABB.max);
      const isBoxEmpty =
        new Vector3().equals(currentAABB.min) &&
        new Vector3().equals(currentAABB.max);

      // We want to update the ortho view in almost every single case, but
      // if no changes happened, we don't want to update, otherwise we risk
      // a janky zoom out when additional geometry is shown or hidden.
      // FIXME: We also reliably get a single janky zoom out when the side bar opens and closes
      // while the geometry is visible. But it doesn't happen on further geometry toggles until
      // the sidebar gets toggled again. We would need to expand what variables we're tracking across
      // frames, and that's out of scope for this current fix. A good place to look for future changes
      // might be around tracking visibleBodies across frames/resize calls.
      if (wasBoxEmpty || isBoxEmpty || !sizeDidNotChange) {
        const viewport = ViewPort.getForBoundingBox(
          currentAABB,
          this.nodeHeight,
          this.nodeWidth,
        );
        this.maxViewport = viewport.clone();
        viewport.applyZoom(this.state.cameraZoom);

        const offset = this.state.orthoView.offset;
        viewport.applyOffset(offset);
        viewport.updateObjectProps(this.camera);
        this.state.onOrthoViewChange(viewport.clone()); //sets new orthoView
      }

      // Update previous AABB with current AABB
      this._prevAABB = currentAABB;
    }

    this.renderer.setSize(this.nodeWidth, this.nodeHeight);
    // Run trackball resize AFTER we set the size of the renderer
    this.trackball.handleResize();
    this.camera.updateProjectionMatrix();

    // Update LineSegments2 resolution for DXF fat lines (only on resize, not every frame)
    if (
      this.state.bodyOrAssemblyInstanceById &&
      newWidth > 0 &&
      newHeight > 0
    ) {
      for (const bodyOrAssembly of this.state.bodyOrAssemblyInstanceById.values()) {
        if (!(bodyOrAssembly instanceof Body) || !bodyOrAssembly.isLoaded)
          continue;
        const obj = bodyOrAssembly.obj;
        if (obj instanceof STEPBody && obj.edgeLineSegments2?.length) {
          for (const lineSegments2 of obj.edgeLineSegments2) {
            const mat = (
              lineSegments2 as unknown as {
                material: {
                  resolution: { set: (x: number, y: number) => void };
                };
              }
            ).material;
            if (mat?.resolution?.set) mat.resolution.set(newWidth, newHeight);
          }
        }
      }
    }

    this.render();
  };

  private handleObjectHover(mousePosition: MouseMarker): void {
    //Note:
    //Exception: vector editor starts with showHoverObject === true
    //Otherwise, currently RendererComponent never updates renderer.state when showHoverObject changes.
    //Initially showHoverObject is false, so this fn (handleObjectHover) is noop
    //If we ever want `onObjectHover` (`onNewHoverPoint` from CADViewer) to be called,
    //RendererComponent needs to call renderer.updateState() when showHoverObject changes.
    //It is an easy change - I have tried it (and left code commented), but note that onMouseMove
    //is fired A LOT, so I am concerned about performance. So if we do start watching showHoverObject,
    //I think there is some additional work to eliminate unnecessary calls and speed up handleFirstObjectIntersect()
    //And handleObjectHover() in viewer-component could be optimized too.
    //Although there is an exception for vector editor, the vector editor doesn't provide the partviewer onNewHoverPoint property
    //so again it has no effect...
    if (this.state.showHoverObject) {
      const nextHoverObjects: AnyIntersectionTest[] = [];
      const intersectionCheck = handleFirstObjectIntersect(mousePosition, this);
      if (intersectionCheck !== EMPTY_INTERSECTION_OBJECT) {
        nextHoverObjects.push({
          ...intersectionCheck,
          obj: intersectionCheck.object,
        });
      }
      //onObjectHover will never be called: See note above (exception is vector editor)
      this.state.onObjectHover(nextHoverObjects);
    }
  }

  //updateHasPMIAnnotations():
  //* Determines if visible bodies have annotations, and if so calls receiveHasPMIAnnotations
  //  so the frontend app (and buttons at top) can be enabled/diabled
  private updateHasPMIAnnotations(
    {
      visibleBodies,
      focusedBodies,
      bodyOrAssemblyInstanceById,
      receiveHasPMIAnnotations,
    }: RendererState = this.state,
  ): void {
    //Note: VectorEditor does not provide receiveHasPMIAnnotations
    if (receiveHasPMIAnnotations) {
      let hasPMIAnnotationsInVisibleBodies = false;
      for (const bodyId of new Set(chain(visibleBodies, focusedBodies))) {
        const bodyOrAssembly = bodyOrAssemblyInstanceById.get(bodyId);
        if (!bodyOrAssembly || !bodyOrAssembly.isRoot) {
          //Today we only render PMI on root
          //If we render PMI on child bodies/assemblyInstances, then the PMI geometry
          //needs to be transformed to correct location of body.
          //Today PMI geometry is located wrt to the part.
          continue;
        }
        const { pmi } =
          (bodyOrAssembly as AnyBody).part ||
          (bodyOrAssembly as AssemblyInstance).assembly ||
          {};
        if (pmi?.hasAnnotations) {
          hasPMIAnnotationsInVisibleBodies = true;
          break;
        }
      }
      receiveHasPMIAnnotations({
        hasPMIAnnotations: hasPMIAnnotationsInVisibleBodies,
      });
    }
  }

  private async updateFocusedBoundBoxAndViewPort(
    nextState: RendererState,
    isAfterVisibilityChange: boolean,
    is2DFileTypeOverride?: boolean,
  ): Promise<boolean> {
    const { bodyOrAssemblyInstanceById, focusedBodies } = nextState;

    if (
      !this._axisAlignedBoundBox ||
      (this.focusedBoundBox && this.state.focusedBodies === focusedBodies)
    ) {
      return false;
    }

    //Dispose of previous focusedBoundBox
    if (this.focusedBoundBox) {
      this.focusedBoundBox.clear();
      this.focusedBoundBox.removeFromParent();
    }

    // re-establish focus for renderer (sets "isFocused" state on viewer-component)
    this.state.onFocusChange(true);

    const hasFocusedBodiesToUse = focusedBodies.some(
      (id) => bodyOrAssemblyInstanceById.get(id)?.isLoaded,
    );

    this.focusedBoundBox = hasFocusedBodiesToUse
      ? prepareBoundBoxForBodies(
          (function* () {
            for (const id of focusedBodies) {
              const body = bodyOrAssemblyInstanceById.get(id);
              if (body) {
                yield body;
              }
            }
          })(),
        )
      : this._axisAlignedBoundBox.clone();
    this.focusedBoundBox.visible = false;

    // call setups on new body loading after preparing new bounding box
    let finalViewport: ViewPort;
    let referenceDiagonal: number;

    if (isAfterVisibilityChange) {
      finalViewport = setUpCameraAndControls(
        this.focusedBoundBox as BoxHelper,
        this._axisAlignedBoundBox as BoxHelper,
        this,
        is2DFileTypeOverride,
      );
      referenceDiagonal = this.maxViewport.diagonal;
      this.state.onOrthoViewChange(finalViewport);
    } else {
      const focusedBoundingBox = this.focusedBoundBox.geometry
        .boundingBox as Box3;

      finalViewport = ViewPort.getForBoundingBox(
        focusedBoundingBox,
        this.nodeHeight,
        this.nodeWidth,
      );

      //Most of the time, boundBoxToUse is the this._axisAlignedBoundBox, but there are edge cases where we fallback to this.focusedBoundBox
      //* In online metals app, visibleBodies is empty for sheetmetal, so use the focusedBoundingBox instead of this._axisAlignedBoundBox
      //  * In rare case where focusedBodies is also empty, recall this.focusedBoundBox is clone of this._axisAlignedBoundBox, so boundBoxToUse is effectively _axisAlignedBoundBox
      const boundBoxToUse = nextState.visibleBodies.length
        ? this._axisAlignedBoundBox
        : this.focusedBoundBox;
      const boundingBox = boundBoxToUse.geometry.boundingBox as Box3;
      const zoom1Viewport = ViewPort.getForBoundingBox(
        boundingBox,
        this.nodeHeight,
        this.nodeWidth,
      );
      referenceDiagonal = zoom1Viewport.diagonal;

      const finalTarget = getMidpoint(
        focusedBoundingBox.min as Vector3,
        focusedBoundingBox.max as Vector3,
      );

      const { cameraPosition: finalPosition, maxDistance } =
        getSafePositionToPreventClippingForOrthographicCamera({
          desiredCameraPosition: this.camera.position.clone(),
          controlTarget: this.trackball.target.clone(),
          scene: this.scene,
        });
      ensureCameraFarSettingCompatibleWithMaxDistance(maxDistance, this.camera);

      // make sure final up is orthogonal to final look vector while maintaining current orientation
      const targetVector = new Vector3()
        .subVectors(finalPosition, finalTarget)
        .normalize();
      const currentUp = this.camera.up.clone();
      const across = new Vector3()
        .crossVectors(targetVector, currentUp)
        .normalize();
      const finalUp = new Vector3().crossVectors(across, targetVector);

      const positionTween = makeCameraPositionTween(
        this,
        finalPosition,
        TWEEN_TIMES.FOCUS,
      );
      const upTween = makeUpTween(this, finalUp, TWEEN_TIMES.FOCUS);
      const orthoTween = makeOrthoCursorTween(
        this,
        nextState.orthoView.isEmpty()
          ? this.state.orthoView
          : nextState.orthoView,
        finalViewport,
        zoom1Viewport, //checkpointViewPort === zoom1Viewport
        //midpointCallBack
        () => setVisibilityForFocus(nextState),
        //finalCallback
        () => {
          setVisibilityForObjectIds(
            nextState,
            chain(nextState.visibleBodies, nextState.visibleStockResultKeys),
          );
        },
        TWEEN_TIMES.FOCUS / 2,
        TWEEN_TIMES.FOCUS / 2,
      );
      const targetTween = makeControlTargetTween(
        this,
        finalTarget,
        TWEEN_TIMES.FOCUS,
      );

      // TODO: stop tweens if snap to tweens are taking place (this was TODO from Dana)
      await Promise.all(
        [orthoTween, targetTween, positionTween, upTween].map((tween) =>
          tween.start(),
        ),
      );
    }

    // turn everything on, but hide all materials, except for any that are focused (or child of focused)
    // NOTE: we can assume that the focus id for material is the root assembly id
    let changed = false;
    for (const bodyOrAssemblyInstance of nextState.bodyOrAssemblyInstanceById.values()) {
      const { group } = bodyOrAssemblyInstance;
      if (bodyOrAssemblyInstance instanceof AssemblyInstance) {
        const { id, isMaterial, isRoot } = bodyOrAssemblyInstance;
        if (isMaterial && isRoot && !focusedBodies.includes(id as string)) {
          if (group.visible) {
            group.visible = false;
            changed = true;
          }
          continue;
        }
      }
      if (!group.visible) {
        //body or assemblyInstance's is not visible
        group.visible = true;
        changed = true;
      }
    }
    // now update body pose for visibility changes
    if (changed) {
      await nextState.onBodyPoseChange(
        getBodyPose(bodyOrAssemblyInstanceById.values()),
      );
    }

    const finalDiagonal = finalViewport.diagonal;
    await this.state.onCameraZoomChange(referenceDiagonal / finalDiagonal);
    this.scaleRadius = finalViewport.getVertexRadius(INTERSECTION_SCALE_FACTOR);

    return true;
  }

  private updateCameraAfterAddingBodies(): void {
    // this will get called after any bodies have been added to the scene
    // confirm that the camera position is not too far away so that the far
    // setting will not render any bodies
    if (this.focusedBoundBox && this._axisAlignedBoundBox) {
      const { maxSceneDistance } = extractDefaultViewProps(
        this.focusedBoundBox,
        this._axisAlignedBoundBox,
        this,
      );
      ensureCameraFarSettingCompatibleWithMaxDistance(
        maxSceneDistance,
        this.camera,
      );
    }

    // Set LineSegments2 resolution for DXF fat lines when bodies first load
    const w = this.nodeWidth;
    const h = this.nodeHeight;
    if (this.state.bodyOrAssemblyInstanceById && w > 0 && h > 0) {
      for (const bodyOrAssembly of this.state.bodyOrAssemblyInstanceById.values()) {
        if (!(bodyOrAssembly instanceof Body) || !bodyOrAssembly.isLoaded)
          continue;
        const obj = bodyOrAssembly.obj;
        if (obj instanceof STEPBody && obj.edgeLineSegments2?.length) {
          for (const lineSegments2 of obj.edgeLineSegments2) {
            const mat = (
              lineSegments2 as unknown as {
                material: {
                  resolution: { set: (x: number, y: number) => void };
                };
              }
            ).material;
            if (mat?.resolution?.set) mat.resolution.set(w, h);
          }
        }
      }
    }
  }

  private run(): void {
    this.isAnimating = true;
    this.requestRenderIfNotRequested();
  }

  private render() {
    this.renderer.clear();
    this.renderer.render(this.scene, this.camera);
    this.renderer.clearDepth();
    this.renderer.render(this.scene2, this.camera);
    this.cube.render();
  }

  private update() {
    TWEEN.update();
    this.cube.update();
    this.updateControls();
  }

  private updateControls() {
    // this.transform.update();
    this.trackball.update();

    //TODO: The following code seems to have no effect?
    //What is point in updating cameraPosition on this.state? (filtering small changes?)
    //Seems to have to do with measurements renderer.scaleRadius and other factors to look at.
    //If we do need to update, why not update in one callback? This could trigger 3 renders

    // check for difference in position on this.state?
    if (!isCloseVectors(this.camera.position, this.state.cameraPosition)) {
      //TODO: Re-home cameraPosition from viewer-component
      this.state.onCameraPositionChange(this.camera.position.clone());
    }
    if (!isCloseVectors(this.camera.up, this.state.cameraUp)) {
      //TODO: Re-home cameraUp from viewer-component
      this.state.onCameraUpChange(this.camera.up.clone());
    }
    // check for difference in control target (pan situation)
    if (!isCloseVectors(this.trackball.target, this.state.controlTarget)) {
      //TODO: Re-home controlTarget from viewer-component
      this.state.onControlTargetChange(this.trackball.target.clone());
    }
  }

  private afterEachLoaded = (
    loaded: AnyPart | AnyBody | AssemblyInstance,
  ): void => {
    if (loaded instanceof Part) {
      for (const bodyId of this.state.bodyIdsByPartFileUuid.get(
        loaded.id as string,
      ) || []) {
        //Now that part is loaded, its associated bodies can have their STEP/STL Body created so that
        //progress estimate for body can be finalized.
        const body = this.state.bodyOrAssemblyInstanceById.get(
          bodyId,
        ) as AnyBody;
        body.createUnhydratedBody();
      }
    } else {
      this.group.add(loaded.boundBox);
      this.scene.add(loaded.additionalFeedbackGroup);
      if (loaded instanceof Body) {
        const { obj } = loaded;
        // update entity intersection counts
        if (obj instanceof STLBody) {
          for (const mesh of obj.meshes) {
            this.intersectionEntities.add(mesh);
          }
        } else {
          // Call to update layers in state
          const { faces, edges, texts, vertices, vectorStyleByNameMap } = obj;
          // If hydrated vectorStyleByNameMap exists, init Map for vectorEditor
          if (
            vectorStyleByNameMap.size > 0 &&
            this.state.initializeVectorStyleByNameMap
          ) {
            this.state.initializeVectorStyleByNameMap(obj);
          }
          for (const { renderable } of vertices.values()) {
            this.intersectionEntities.add(renderable as Intersectable);
          }
          for (const intersectable of chain<Intersectable>(
            faces.values(),
            edges.values(),
            texts.values(),
          )) {
            this.intersectionEntities.add(intersectable);
          }
        }
      }
    }
  };

  private onLoadFailed = (
    failedLoadable: AnyPart | AnyBody | AssemblyInstance,
  ): void => {
    const id = failedLoadable.id as string;
    if (failedLoadable instanceof Part) {
      //remove part because there was a problem loading its geometry
      console.warn(
        `Part ${id}: (${failedLoadable.name}) removed because there was a failure loading its geometry`,
      );
      this.state.partOrAssemblyById.delete(id);
      for (const bodyId of this.state.bodyIdsByPartFileUuid.get(id) || []) {
        this.removeBody(bodyId);
      }
    } else if (failedLoadable instanceof Body) {
      this.removeBody(id);
    } else if (failedLoadable instanceof AssemblyInstance) {
      for (const asmInst of this.state.bodyOrAssemblyInstanceById.values()) {
        if (asmInst instanceof AssemblyInstance) {
          if (asmInst.removeChild(failedLoadable)) {
            console.warn(
              `AssemblyInstance ${failedLoadable.id} (${failedLoadable.name}) removed from ${asmInst.id} (${asmInst.name}) because there was a failure loading`,
            );
          }
        }
      }
      this.state.bodyOrAssemblyInstanceById.delete(id);
      this.loadingPriorityQueue.removeTasksFor(failedLoadable);
    }
  };

  //removeBody() is for use in onLoadFailed()
  private removeBody(bodyId: string): void {
    //* remove all bodies for that part because if the part doesn't have geometry, the bodies won't either
    //* remove the body from the loadingPriorityQueue because we know it won't load
    //* remove body from assemblyInstances that may reference it
    const body = this.state.bodyOrAssemblyInstanceById.get(bodyId);
    if (body) {
      this.loadingPriorityQueue.removeTasksFor(body);
      for (const asmInst of this.state.bodyOrAssemblyInstanceById.values()) {
        if (asmInst instanceof AssemblyInstance) {
          if (asmInst.removeChild(body)) {
            console.warn(
              `AssemblyInstance ${body.id} (${body.name}) removed from ${asmInst.id} (${asmInst.name}) because there was a failure loading`,
            );
          }
        }
      }
    }
    this.state.bodyOrAssemblyInstanceById.delete(bodyId);
  }

  private everyVisibleBodyLoaded(state: RendererState): boolean {
    //* Note if visibleBodyId is unknown, it is because it was removed by onLoadFailed() (filtered out)
    //* This test is about all visibleBodies that were possible to load
    return state.visibleBodies.every((visibleBodyId) => {
      const bodyOrAssemblyInstance =
        this.state.bodyOrAssemblyInstanceById.get(visibleBodyId);
      return !bodyOrAssemblyInstance || bodyOrAssemblyInstance.isLoaded;
    });
  }

  //everyVisibleBodyConstructed: every body exists, but not necessary loaded
  private everyVisibleBodyConstructed(state: RendererState): boolean {
    return state.visibleBodies.every((visibleBodyId) =>
      this.state.bodyOrAssemblyInstanceById.has(visibleBodyId),
    );
  }

  private afterEachPriorityLevelLoaded = (
    nextState: RendererState,
    //priorityLevel: number,
  ): void => {
    //Every body and assemblyInstance should be loaded
    if (this.everyVisibleBodyLoaded(nextState)) {
      this.updateBoundBox(nextState);

      //force updating visibility: There are edge cases when going to/from part library and back to a part where
      //the geometry group additionalFeedbackGroup is not toggled correctly
      //* In these situations, this.state and nextState can't be compared properly
      //* This fn is called only when the visibleBodies changes and after all bodies are loaded, so it is not a performance
      //  issue to force it to update.
      handleUpdateVisibleBodies(this, nextState, true);

      // Compute 2D file type from nextState since this.state hasn't been updated yet.
      // This ensures correct camera positioning for DXF/DWG files during file switches.
      const is2DFileType = this.computeIs2DFileTypeForState(nextState);
      this.updateFocusedBoundBoxAndViewPort(nextState, true, is2DFileType);

      this.updateCameraAfterAddingBodies();

      this.updatePartPMIVisibility(nextState);

      this.updateState(nextState, true);

      if (nextState.visibleBodies.length > 0) {
        // Hide the orientation cube for 2D files (DXF/DWG) since we're always looking straight on
        // Reuse the is2DFileType computed earlier from nextState
        if (is2DFileType) {
          this.cube.hide();
        } else {
          this.cube.show(); //if already loaded and shown, this is noop
        }
      }

      //force paint: There are edge cases when going to/from part library and back to a part where
      //the geometry aren't re-painted properly (visibility is left off)
      //* This fn is called only when the visibleBodies changes and after all bodies are loaded, so it is not a performance
      //  issue to force it to update.
      handleUpdatePaint(this.state, nextState, true);

      //Using the budget of `maxVerticesInBuffer`, set the representation of each body to use; preferring full detail for largest bodies first!
      const visibleBodiesSortedBySize = Array.from(
        getAllAffectedBodies(
          nextState.bodyOrAssemblyInstanceById,
          nextState.visibleBodies,
        ),
      ).sort(compareByOBBMaxSideArea);
      let verticesUsed = visibleBodiesSortedBySize.length * 48; //simp reps have 24 vertices in their mesh and 24 vertices in their wireframe (12 triangle faces)
      const tooComplex = new Set<string>(); //part ids
      const temporarilySimplified = new Set<string>(); //body ids
      const temporarilySimplifiedParts = new Set<string>(); //part ids of bodies that are temporarily simplified
      for (const body of visibleBodiesSortedBySize) {
        const bodyVerticesCount = body.geometryVerticesCount;
        if (
          body.obj instanceof STLBody &&
          bodyVerticesCount > maxVerticesInSTL
        ) {
          body.activeRepresentation = ActiveRepresentation.OBBSimplifiedRep;
          const simpRep = body.obbSimplifiedRep;
          if (simpRep) {
            simpRep.setColorTooComplex();
            if (body.part.id) {
              tooComplex.add(body.part.id);
            }
          }
        } else if (bodyVerticesCount > maxVerticesInBody) {
          //Will use the simp rep, with the too complex color
          body.activeRepresentation = ActiveRepresentation.OBBSimplifiedRep;
          const simpRep = body.obbSimplifiedRep;
          if (simpRep) {
            simpRep.setColorTooComplex();
            if (body.part.id) {
              tooComplex.add(body.part.id);
            }
          }
        } else {
          if (verticesUsed + bodyVerticesCount > maxVerticesAcrossAllBuffers) {
            //already accounted for 48 vertices for simp rep above
            body.activeRepresentation = ActiveRepresentation.OBBSimplifiedRep;
            if (body.id) {
              temporarilySimplified.add(body.id);
            }
            if (body.part.id) {
              temporarilySimplifiedParts.add(body.part.id);
            }
          } else {
            verticesUsed += bodyVerticesCount - 48; //already accounted for 48 above
            body.activeRepresentation = ActiveRepresentation.FullDetailRep;
          }
        }
      }
      if (
        areSetsDifferent(this.temporarilySimplified, temporarilySimplified) ||
        areSetsDifferent(this.tooComplex, tooComplex)
      ) {
        this.temporarilySimplified = temporarilySimplified;
        this.tooComplex = tooComplex;
        this.state.onSimplified({
          temporarilySimplified,
          temporarilySimplifiedParts,
          tooComplex,
        });
        if (temporarilySimplified.size) {
          console.warn(
            `${temporarilySimplified.size} of ${visibleBodiesSortedBySize.length} bodies are temporarily simplified`,
          );
        }
        if (tooComplex.size) {
          console.warn(
            `${tooComplex.size} of ${visibleBodiesSortedBySize.length} bodies are too complex to show in this viewer`,
          );
        }
      }

      if (nextState.visibleBodies.length > 0) {
        if (this.firstLoad) {
          this.state.onHasLoaded({
            hasLoaded: true,
          });
          delete this.firstLoad;
        }
        if (this.state.setReadyForInteraction) {
          //This is for pcad
          this.state.setReadyForInteraction({ isReady: true });
        }
        this.run();
        this.state.loadingProgress.clearDone();
        this.state.loadingProgress.updateVisibleIds([]);
      }
    }
  };

  //updateAxisAlignedDimensions():
  //* Important: this.updateBoundBox() should be called before
  private updateAxisAlignedDimensions(nextState: RendererState) {
    //Before creating, Remove this._dimensions from scene2 and dispose
    if (this._axisAlignedDimensions) {
      if (this.scene2.getObjectByName(this._axisAlignedDimensions.id)) {
        this.scene2.remove(this._axisAlignedDimensions.group);
      }
      this._axisAlignedDimensions.dispose();
    }

    if (this._axisAlignedBoundBox?.visible) {
      const dimensions = new Dimensions(
        this._axisAlignedBoundBox?.geometry.boundingBox as Box3,
      );
      dimensions.makeSpriteMaterial({
        displayUnits: nextState.displayUnits,
        nativeUnits: nextState.nativeUnits,
        dimensionalPrecision: nextState.dimensionalPrecision,
        displayScale: nextState.displayScale,
      });
      dimensions.scaleForOrthographicCamera({ viewport: nextState.orthoView });
      dimensions.group.visible = true;
      this._axisAlignedDimensions = dimensions;
    }
  }

  private savePartViewerImage(filename = `parviewer-${Date.now()}`): void {
    //Ensure the render and callback canvasToPng() happen at the same time
    setTimeout(() => {
      requestAnimationFrame(() => {
        this.animate();
        this.canvasToPng(this.renderer.domElement, filename);
      });
    }, inverseFrameRate);
  }

  private saveCubeImage(filename = `cube-${Date.now()}`): void {
    //Ensure the render and callback canvasToPng() happen at the same time
    setTimeout(() => {
      requestAnimationFrame(() => {
        this.animate();
        this.canvasToPng(this.cube.renderer.domElement, filename);
      });
    }, inverseFrameRate);
  }

  private canvasToPng(canvas: HTMLCanvasElement, filename: string): void {
    const image = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = image.replace(
      /^data:image\/[^;]/,
      'data:application/octet-stream',
    );
    a.download = `${filename}.png`;
    a.click();
  }
}

export default Renderer;
